import './multiselect.scss';

import { SwapOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import { forwardRef, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';

import type { IInputProps } from '../input';
import Input from '../input';
import OutsideClickHandler from '../outside-click-handler';
import useControls from './hooks/useControls';
import usePopup from './hooks/usePopup';
import useSelect from './hooks/useSelect';

interface IProps extends Omit<IInputProps, 'onChange' | 'value'> {
  options: IMultiselectOptions[];
  selectedValues?: IMultiselectOptions[];
  onChange: (
    value: IMultiselectOptions | null,
    selectedValues: IMultiselectOptions[]
  ) => void;
}

const Multiselect = forwardRef<HTMLInputElement, IProps>(
  ({ selectedValues, options, onChange, isFrozen, ...rest }, forwardRef) => {
    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);
    const [focusTriggered, setFocusTriggered] = useState(false);
    // Идентификаторы для связки combobox → listbox → активная опция (ARIA)
    const listboxId = useId();
    const optionId = (index: number) => `${listboxId}-option-${index}`;

    const {
      filteredOptions,
      checkedFilter,
      searchValue,
      setSearchValue,
      toggleCheckedFilter
    } = useControls({
      selectedValues,
      options
    });

    const { bindInput, bindOptions, bindOption, selectedIndex } = useSelect({
      selectedValues,
      onChange,
      defaultOptions: filteredOptions,
      setFocused
    });

    const { topPosition, handlePopupToggle } = usePopup({
      focused,
      setFocused,
      listElement: bindOptions.ref.current,
      isFrozen
    });

    const handleAll = () => {
      if (options.length === selectedValues?.length) onChange(null, []);
      else onChange(options[0], options);
    };

    const fieldClassName = [rest.className ?? '', 'multiselect__field'].join(
      ' '
    );

    return (
      <OutsideClickHandler
        onOutsideClick={() => {
          setSearchValue('');
          setFocused(false);
        }}
      >
        <div className="multiselect">
          <Input
            {...rest}
            readOnly
            inputMode="none"
            ref={forwardRef}
            className={fieldClassName}
            value={bindInput.value ?? ''}
            autoComplete="off"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={focused}
            aria-controls={listboxId}
            aria-activedescendant={
              focused && selectedIndex >= 0
                ? optionId(selectedIndex)
                : undefined
            }
            endContent={
              <span
                aria-hidden="true"
                className={`multiselect__arrow ${
                  focused ? 'multiselect__arrow--rotated' : ''
                }`}
              >
                <ChevronDownIcon />
              </span>
            }
            onChange={() => null}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && focused) e.preventDefault();
              if (e.key === 'Tab') setFocused(false);
              bindInput.onKeyDown(e);
              rest.onKeyDown?.(e);
            }}
            onFocus={(e) => {
              setFocusTriggered(true);
              handlePopupToggle();
              if (focused) setSearchValue('');
              rest.onFocus?.(e);
            }}
            onClick={(e) => {
              if (!focusTriggered) {
                handlePopupToggle();
                if (focused) setSearchValue('');
              }
              setFocusTriggered(false);
              rest.onClick?.(e);
            }}
            isFrozen={isFrozen}
          />
          <div
            className={`multiselect__popup ${
              focused ? 'multiselect__popup--shown' : ''
            } ${topPosition ? 'multiselect__popup--to-top' : ''}`}
          >
            <div className="multiselect__controls">
              <Checkbox
                aria-label={t('a11y.selectAll')}
                indeterminate={
                  (selectedValues?.length || 0) > 0 &&
                  options.length !== selectedValues?.length
                }
                checked={options.length === selectedValues?.length}
                onChange={handleAll}
              />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="multiselect__search"
                placeholder={t('common.search')}
                aria-label={t('common.search')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && focused) e.preventDefault();
                  bindInput.onKeyDown(e);
                }}
              />
              <button
                type="button"
                aria-label={t('a11y.showSelected')}
                aria-pressed={checkedFilter}
                onClick={toggleCheckedFilter}
                className={`multiselect__filter ${
                  checkedFilter ? 'multiselect__filter--selected' : ''
                } `}
              >
                <SwapOutlined aria-hidden="true" />
              </button>
            </div>

            <ul
              {...bindOptions}
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              className="multiselect__list"
            >
              {filteredOptions.map((option, index) => {
                const isChecked =
                  selectedValues?.find(
                    (selected) => selected.value === option.value
                  ) !== undefined;
                return (
                  <li
                    className={`multiselect__item ${
                      selectedIndex === index
                        ? 'multiselect__item--selected'
                        : ''
                    }`}
                    key={option.label + option.value}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isChecked}
                    {...bindOption}
                  >
                    {/* Чекбокс декоративный: выбором управляет сама опция,
                        состояние скринридер читает из aria-selected */}
                    <span aria-hidden="true">
                      <Checkbox checked={isChecked} tabIndex={-1} />
                    </span>
                    {option.label}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
);

Multiselect.displayName = 'Multiselect';

export default Multiselect;

export interface IMultiselectOptions {
  value: string;
  label: string;
}
